/**
 * Asset host of a Storyblok space in the EU region. The `storyblok` image
 * provider rebuilds every src onto this host (`image.storyblok.baseURL`).
 */
export const STORYBLOK_ASSET_HOST = "a.storyblok.com";

/**
 * An image-service suffix after the filename (`a.avif/m/`, `a.avif/m/600x0`,
 * `a.avif/m/fit-in/600x0/filters:…`) — anchored to a file extension, so an
 * asset folder named `m` is not mistaken for one.
 */
export const STORYBLOK_TRANSFORM_SUFFIX = /(?<=\.[a-z0-9]+)\/m\/.*$/i;

export interface CmsImageSource {
	src: string;
	/** Set only for assets the Storyblok image service can transform. */
	provider?: "storyblok";
}

/**
 * `src` and `provider` for a `<NuxtImg>` showing a CMS image.
 *
 * The `storyblok` provider builds `/m/<w>x0/filters:…` URLs from the src's
 * pathname alone and puts them on a.storyblok.com, so only assets on that
 * host may use it. A regional space's host (a-us, a-ap, a-ca), the Norce CDN
 * or any other URL keeps NuxtImg's default provider, which returns an
 * absolute URL unchanged. A Storyblok src loses its query and hash, which the
 * asset host ignores anyway, and any transform it already carries. An SVG
 * stays on the default provider: the service does not transform it, and the
 * provider's own SVG check is case-sensitive. Empty input yields `undefined`:
 * render no image, so nothing is requested.
 */
export const toCmsImageSource = (
	url: string | null | undefined
): CmsImageSource | undefined => {
	const trimmed = url?.trim();
	if (!trimmed) return undefined;

	const absolute = trimmed.startsWith("//") ? `https:${trimmed}` : trimmed;

	try {
		const parsed = new URL(absolute);
		if (parsed.hostname === STORYBLOK_ASSET_HOST) {
			const assetPath = parsed.pathname.replace(STORYBLOK_TRANSFORM_SUFFIX, "");
			const src = `https://${STORYBLOK_ASSET_HOST}${assetPath}`;
			return /\.svg$/i.test(assetPath)
				? { src }
				: { src, provider: "storyblok" };
		}
	} catch {
		// Not an absolute URL — nothing the image service could transform.
	}

	return { src: trimmed };
};
